import { Component, Input, OnInit } from '@angular/core';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import EmployeeHelper from 'src/app/helpers/employee-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { NotExecutedHistoryItem } from 'src/app/model/history-item/not-executed-history-item';
import Employee from 'src/app/model/product/employee';
import { BaseProject } from 'src/app/model/project/base-project';
import { Project } from 'src/app/model/project/project';
import { ImmoExecutionInfoControllerRequestService } from 'src/app/services/api/immo-execution-info/immo-execution-info-controller-request.service';
import {
  ProjectDisplayObservableService,
  TourConfigurationChangedEvent
} from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';

@Component({
  selector: 'execution-card',
  templateUrl: './execution-card.component.html',
  styleUrls: ['./execution-card.component.scss']
})
export class ExecutionCardComponent extends SubscriptionBasedComponent
  implements OnInit {
  @Input()
  project: Project;

  @Input()
  baseProject: BaseProject;

  @Input()
  doFetchTourInfo: boolean = false;

  @Input()
  componentsExecuted: boolean | null = null;

  executed: boolean = false;
  notExecuted: boolean = false;
  installerFeedback: string;
  tourDateDisplay: string;
  tourInstallerDisplay: string;

  notExecutedHistoryItem: NotExecutedHistoryItem;

  constructor(
    private immoExecutionInfoControllerRequestService: ImmoExecutionInfoControllerRequestService,
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit(): void {
    this.handleTourInfo();
    this.handleExecutionInfo();

    this.addSubscription(
      this.projectDisplayObservableService.getProductExecutedSubscribable(),
      () => this.displayPlacementDate()
    );
  }

  private handleTourInfo(): void {
    if (this.doFetchTourInfo || this.baseProject.fromOldSystem) {
      this.getSubProjectImmoExecutionInfo();
    } else {
      this.addSubscription(
        this.projectDisplayObservableService.getTourConfigurationChangedSubscribable(),
        (event: TourConfigurationChangedEvent) => {
          if (event.subProjectId === this.baseProject.id) {
            this.setTourDisplays(
              event.assignedToTour,
              event.date,
              event.placer,
              event.notExecutedHistoryItem
            );
          }
        }
      );
    }
  }

  private handleExecutionInfo(): void {
    this.executed = ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(
      this.baseProject.status
    );
    if (!this.executed) {
      this.notExecuted = ProjectHelper.NOT_EXECUTED_STATUSES.includes(
        this.baseProject.status
      );
    }
    const productWithFeedback = !CollectionHelper.isPopulated(
      this.project.products
    )
      ? null
      : this.project.products.find(
          p =>
            !StringHelper.isEmpty(p['installerFeedback']) ||
            (p.productPlacement != null &&
              !StringHelper.isEmpty(p.productPlacement.installerFeedback))
        );
    this.installerFeedback =
      productWithFeedback == null
        ? null
        : !StringHelper.isEmpty(productWithFeedback['installerFeedback'])
        ? productWithFeedback['installerFeedback']
        : productWithFeedback.productPlacement.installerFeedback;
  }

  private async getSubProjectImmoExecutionInfo(): Promise<void> {
    if (this.baseProject.id == null && this.baseProject.fromOldSystem) {
      this.setTourDisplays(
        false,
        this.baseProject.currentExecutionDate,
        null,
        null
      );
      return;
    }
    const immoExecutionInfo = await this.immoExecutionInfoControllerRequestService.getImmoExecutionInfo(
      this.baseProject.id
    );
    if (immoExecutionInfo != null) {
      this.setTourDisplays(
        immoExecutionInfo.assignedToTour,
        immoExecutionInfo.date,
        immoExecutionInfo.placer,
        immoExecutionInfo.notExecutedHistoryItem
      );
    }
  }

  private setTourDisplays(
    assignedToTour: boolean,
    date: Date,
    placer: Employee,
    notExecutedHistoryItem: NotExecutedHistoryItem
  ): void {
    if (notExecutedHistoryItem != null) {
      this.notExecutedHistoryItem = notExecutedHistoryItem;
      if (notExecutedHistoryItem.assets != null) {
        this.projectDisplayObservableService
          .getNotExecutedHistoryItemAssetsFetchedSubscribable()
          .next({
            subProjectId: this.baseProject.id,
            assets: notExecutedHistoryItem.assets
          });
      }
    } else {
      this.notExecutedHistoryItem = null;
    }
    if (date != null) {
      this.tourDateDisplay = DateHelper.formatDate(date, 'dd/MM/yyyy');
    } else if (!assignedToTour) {
      const placementDate = ProjectHelper.getMostRecentPlacementDate(
        this.project
      );
      this.tourDateDisplay = DateHelper.formatDate(placementDate, 'dd/MM/yyyy');
    }
    this.tourInstallerDisplay =
      placer == null ? null : EmployeeHelper.getName(placer);
  }

  private displayPlacementDate() {
    const placementDate = ProjectHelper.getMostRecentPlacementDate(
      this.project
    );
    this.tourDateDisplay = DateHelper.formatDate(
      placementDate != null
        ? placementDate
        : this.baseProject.currentExecutionDate,
      'dd/MM/yyyy'
    );
  }
}
